import { EntityDto } from 'app/models/generated'
import { logger } from 'app/logging/Logger'
import {
    executeQuery,
    executeMutation,
    fetchVersion,
} from 'app/api/client/graphqlClient'
import {
    consultEntityDtosByConsultId,
    createConsultEntityDtoMutation,
    updateConsultEntityDtoMutation,
    deleteConsultEntityDtoMutation,
} from 'app/graphql/customQueries'
import { isNotDeleted } from './softDelete'

export interface EntityRepository {
    /**
     * Get entities for a consult
     * @param consultId The consult ID
     * @returns The entities or null if not found
     */
    getByConsultId(consultId: string): Promise<EntityDto[] | null>

    /**
     * Get entities for multiple consults in a single batch query
     * @param consultIds Array of consult IDs
     * @returns Map of consultId to entities
     */
    getBatchByConsultIds(
        consultIds: string[]
    ): Promise<Map<string, EntityDto[]>>

    /**
     * Create or update entities for a consult
     * @param consultId The consult ID
     * @param entities The entities to save
     */
    saveEntities(consultId: string, entities: EntityDto[]): Promise<void>

    /**
     * Delete entities for a consult
     * @param consultId The consult ID
     */
    deleteEntities(consultId: string): Promise<void>
}

interface ConsultEntityDtoResult {
    id: string
    consultId: string
    owner?: string
    entities: EntityDto[]
}

interface ConsultEntityDtosByConsultIdResult {
    items: ConsultEntityDtoResult[]
    nextToken: string | null
}

export class DataStoreEntityRepository implements EntityRepository {
    /**
     * Get entities for multiple consults using individual GSI queries per consult ID.
     */
    async getBatchByConsultIds(
        consultIds: string[]
    ): Promise<Map<string, EntityDto[]>> {
        try {
            const entityMap = new Map<string, EntityDto[]>()

            const results = await Promise.all(
                consultIds.map(async (consultId) => {
                    const result =
                        await executeQuery<ConsultEntityDtosByConsultIdResult>(
                            consultEntityDtosByConsultId,
                            { consultId },
                            'consultEntityDtosByConsultId'
                        )
                    return { consultId, result }
                })
            )

            results.forEach(({ consultId, result }) => {
                const items = result.items.filter(isNotDeleted)
                if (items.length > 0 && items[0].entities?.length > 0) {
                    entityMap.set(consultId, items[0].entities)
                }
            })

            logger.debug('Batch loaded entities for consults', {
                action: 'batch_get_entities',
                metadata: {
                    consultCount: consultIds.length,
                    entitiesFoundCount: entityMap.size,
                },
            })

            return entityMap
        } catch (error) {
            logger.error(
                'Failed to batch fetch entities',
                error instanceof Error ? error : new Error(String(error)),
                {
                    action: 'batch_get_entities_error',
                    metadata: { consultIds, error: String(error) },
                }
            )
            throw error
        }
    }

    async getByConsultId(consultId: string): Promise<EntityDto[] | null> {
        try {
            const result =
                await executeQuery<ConsultEntityDtosByConsultIdResult>(
                    consultEntityDtosByConsultId,
                    { consultId },
                    'consultEntityDtosByConsultId'
                )

            const items = result.items.filter(isNotDeleted)
            if (items.length === 0) {
                return null
            }

            return items[0].entities || []
        } catch (error) {
            logger.error(
                'Failed to fetch entities',
                error instanceof Error ? error : new Error(String(error)),
                {
                    action: 'get_entities_by_consult_id',
                    metadata: { consultId, error: String(error) },
                }
            )
            throw error
        }
    }

    async saveEntities(
        consultId: string,
        entities: EntityDto[]
    ): Promise<void> {
        try {
            // Check if entity record already exists via GSI query
            const result =
                await executeQuery<ConsultEntityDtosByConsultIdResult>(
                    consultEntityDtosByConsultId,
                    { consultId },
                    'consultEntityDtosByConsultId'
                )

            const existing = result.items.filter(isNotDeleted)

            if (existing.length > 0) {
                // Update existing record: fetch version then update
                const existingId = existing[0].id
                const _version = await fetchVersion(
                    'ConsultEntityDto',
                    existingId
                )

                await executeMutation(
                    updateConsultEntityDtoMutation,
                    {
                        input: {
                            id: existingId,
                            entities,
                            _version,
                        },
                    },
                    'updateConsultEntityDto'
                )

                logger.info('Entities updated successfully', {
                    action: 'update_entities',
                    metadata: { consultId, entityCount: entities.length },
                })
            } else {
                // Create new record (id = consultId for 1:1 relationship)
                await executeMutation(
                    createConsultEntityDtoMutation,
                    {
                        input: {
                            id: consultId,
                            consultId,
                            entities,
                        },
                    },
                    'createConsultEntityDto'
                )

                logger.info('Entities created successfully', {
                    action: 'create_entities',
                    metadata: { consultId, entityCount: entities.length },
                })
            }
        } catch (error) {
            logger.error(
                'Failed to save entities',
                error instanceof Error ? error : new Error(String(error)),
                {
                    action: 'save_entities',
                    metadata: { consultId, error: String(error) },
                }
            )
            throw error
        }
    }

    async deleteEntities(consultId: string): Promise<void> {
        try {
            // Query existing by consultId
            const result =
                await executeQuery<ConsultEntityDtosByConsultIdResult>(
                    consultEntityDtosByConsultId,
                    { consultId },
                    'consultEntityDtosByConsultId'
                )

            const existing = result.items.filter(isNotDeleted)

            if (existing.length > 0) {
                const existingId = existing[0].id
                const _version = await fetchVersion(
                    'ConsultEntityDto',
                    existingId
                )

                await executeMutation(
                    deleteConsultEntityDtoMutation,
                    {
                        input: {
                            id: existingId,
                            _version,
                        },
                    },
                    'deleteConsultEntityDto'
                )

                logger.info('Entities deleted successfully', {
                    action: 'delete_entities',
                    metadata: { consultId },
                })
            }
        } catch (error) {
            logger.error(
                'Failed to delete entities',
                error instanceof Error ? error : new Error(String(error)),
                {
                    action: 'delete_entities',
                    metadata: { consultId, error: String(error) },
                }
            )
            throw error
        }
    }
}

// Singleton instance
export const entityRepository = new DataStoreEntityRepository()
